import { createContext, useCallback, useContext, useMemo } from 'react';
import { workItemViewStoreSlot } from '@/stores/workItemViewStoreSlot';
import { useLocation } from 'react-router-dom';
import { useStores } from '@/hooks/useStores';
import { projectDecisionPath, projectItemPath } from './projectItemUrls';
import { itemPagePath } from './fullPagePaths';
import { originOf, type IFullPageState } from './fullPageOrigin';
import { workAreaRouteRefOf, useWorkAreaRouteRef } from '@/routing/workAreaRoute';
import { usePhoneNavigation } from '@/shell/usePhoneNavigation';
import {
  allWorkAreasItemHref,
  isWorkAreaOverviewPath,
  activityFeedOfItem,
  workAreaActivityFeedOfItem,
  workAreaActivityItemPath,
  activityItemPath,
  namespaceDecisionPath,
  namespaceIdOfPath,
  workAreasDecisionPath,
} from '@/features/work-areas/workAreaOverviewUrls';

/**
 * The work area the item being shown belongs to, for a host that shows one outside its work area's
 * route (I-0689). Without it, an item's links resolve against the route's slug or, with none, the
 * remembered work area: under All work areas that is a guess, and can be another item with the key.
 */
export const ItemLinkWorkAreaContext = createContext<string | null>(null);

/**
 * Builds the href for opening a work item while keeping the current list view
 * intact — see the URL convention in `useWorkItemUrlSync`. Selection lives in
 * the path, the view lives in the query, so opening an item from a filtered
 * list must carry that filter along.
 *
 * Returned as an href rather than a click handler on purpose: rows and links
 * stay real anchors, so middle-click, cmd-click and "copy link" all work.
 */
export interface IItemHrefOptions {
  /** UI-only list occurrence for duplicate hierarchy rows; the `focus` query. */
  occurrenceId?: string | null;
  /** One comment on the item to align at the top of its pane; the `comment` query (I-0104). */
  commentId?: string | null;
}

/** Where an item opens, and what the open carries (a full page's origin, which holds the scope). */
export interface IItemOpen {
  readonly to: string;
  readonly state?: IFullPageState;
}

/**
 * An item opened from a page under All work areas, named by its work area's id (I-0689, I-1304,
 * I-1357).
 *
 * ⛔ **The one place that chooses.** A phone opens the item at its own pushed address, in its work
 * area, with the back row a list tap gives. A desktop opens the item's full page in its work area
 * (I-1357, IL-19f: the panel over the page is gone), carrying where the reader was (`cameFrom`), so
 * the page stays in All work areas and Back pops to the row's page. A row, a notification, the
 * command palette and a link in text all ask here, and `internalLinks.guard.test.ts` refuses another
 * caller of `allWorkAreasItemHref`, which remains only as the address of an item whose work area is
 * not read yet: that address is replaced by the item's page once it is (`AllWorkAreasItemPanel`).
 */
export function useAllWorkAreasItemOpen(): (workAreaId: string, key: string) => IItemOpen {
  const { projectStore } = useStores();
  const location = useLocation();
  const phone = usePhoneNavigation();
  return useCallback(
    (workAreaId: string, key: string) => {
      const ref = workAreaRouteRefOf(projectStore.projectMap?.[workAreaId]);
      if (!ref) return { to: allWorkAreasItemHref(location, workAreaId, key) };
      if (phone) return { to: projectItemPath(ref, key) };
      return {
        to: itemPagePath(ref, key),
        state: { cameFrom: originOf(location, { kind: 'all' }) },
      };
    },
    [phone, projectStore, location],
  );
}

/** `useAllWorkAreasItemOpen`'s address alone, for a caller that draws an href. */
export function useAllWorkAreasItemHref(): (workAreaId: string, key: string) => string {
  const open = useAllWorkAreasItemOpen();
  return useCallback((workAreaId: string, key: string) => open(workAreaId, key).to, [open]);
}

export function useItemHref(): (
  key: string,
  occurrenceOrOptions?: string | null | IItemHrefOptions,
) => string {
  const { projectStore } = useStores();
  const routeProjectSlug = useWorkAreaRouteRef();
  const { pathname } = useLocation();
  const shownWorkAreaId = useContext(ItemLinkWorkAreaContext);
  // T-0892 (a): the list's view state loads with the items pages; until then the list is not shown,
  // and its type and full-page choice are the defaults. The slot's `peek()` is observable.
  const view = workItemViewStoreSlot.peek();
  const typeKey = view?.typeKey ?? '';
  const fullView = view?.fullView ?? false;
  const shownWorkArea = shownWorkAreaId ? projectStore.projectMap?.[shownWorkAreaId] : undefined;
  const projectSlug =
    workAreaRouteRefOf(shownWorkArea) ??
    routeProjectSlug ??
    workAreaRouteRefOf(projectStore.selectedProject);
  const scopeItemHref = useAllWorkAreasItemHref();
  // I-0689: under All work areas an item opens in the panel over this page, never in its work area.
  const inScopeWorkAreaId = isWorkAreaOverviewPath(pathname)
    ? (shownWorkAreaId ??
      projectStore.projects?.find((project) => workAreaRouteRefOf(project) === routeProjectSlug)
        ?.id ??
      null)
    : null;

  // T-0565, T-0566: on an item pushed from a scope's Activity feed, its links push too, in that scope.
  const pushedFrom = activityFeedOfItem(pathname);
  // IU-12b: on an item pushed from a work area's own Activity, its links push there too.
  const pushedFromWorkArea = workAreaActivityFeedOfItem(pathname);
  // The pushed screen names its item's work area (`ItemLinkWorkAreaContext`); an organisation's
  // address is not an All work areas one, so that is where the work area is read from there.
  const pushedWorkAreaId = pushedFrom ? (shownWorkAreaId ?? inScopeWorkAreaId) : null;
  const pushedRef = pushedWorkAreaId
    ? workAreaRouteRefOf(projectStore.projectMap?.[pushedWorkAreaId])
    : undefined;

  /*
   * I-1124: a list draws its rows' hrefs again on every move of its window, for every row drawn, and
   * building each from the route (`workAreaRoutePath`, a parse) and a fresh `URLSearchParams` was most
   * of a block's render at 4×, measured with V8's profiler. The view's query is worked out once, and
   * each row's href once per list view (by `projectItemPath`, the one builder of an item's address,
   * I-1264): the cache lives as long as the work area and view it was built for.
   */
  const viewQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (typeKey) params.set('type', typeKey);
    if (fullView) params.set('view', 'full');
    return params.toString();
  }, [typeKey, fullView]);
  // A new cache for every route and view the hrefs are built for.
  const rowHrefs = useMemo(() => new Map<string, string>(), [projectSlug, viewQuery]);

  return useCallback(
    (key: string, occurrenceOrOptions?: string | null | IItemHrefOptions) => {
      if (pushedFrom && pushedRef) return activityItemPath(pushedFrom, pushedRef, key);
      if (pushedFromWorkArea) return workAreaActivityItemPath(pushedFromWorkArea, key);
      if (inScopeWorkAreaId) return scopeItemHref(inScopeWorkAreaId, key);
      const options: IItemHrefOptions =
        typeof occurrenceOrOptions === 'object' && occurrenceOrOptions !== null
          ? occurrenceOrOptions
          : { occurrenceId: occurrenceOrOptions };
      const cacheKey = `${key}\u0000${options.occurrenceId ?? ''}\u0000${options.commentId ?? ''}`;
      const cached = rowHrefs.get(cacheKey);
      if (cached !== undefined) return cached;
      const params = new URLSearchParams(viewQuery);
      if (options.occurrenceId) params.set('focus', options.occurrenceId);
      if (options.commentId) params.set('comment', options.commentId);
      const href = projectItemPath(projectSlug, key, params.toString());
      rowHrefs.set(cacheKey, href);
      return href;
    },
    [
      rowHrefs,
      viewQuery,
      projectSlug,
      inScopeWorkAreaId,
      scopeItemHref,
      pushedFrom,
      pushedRef,
      pushedFromWorkArea,
    ],
  );
}

/** An item a link points at: its key, and its work area when that may not be the current one. */
export interface ILinkedItem {
  key: string;
  workAreaId?: string | null;
  /** Its type, where the caller knows it: a decision opens at a decision's address (I-1304). */
  itemTypeKey?: string | null;
}

/** The item type a decision is filed as; it opens at a decision's address (I-1304). */
export const DECISION_TYPE_KEY = 'decision';

/**
 * The href and the label for an item that may live in another work area — a decision's `blocks` and
 * `relates` may cross work areas (I-0423). Keys repeat across work areas, so another area's item is
 * labelled `slug:KEY` and opened in its own work area; an item here is opened as `useItemHref` would.
 */
export function useLinkedItemHref(): (item: ILinkedItem) => { href: string; label: string } {
  const { projectStore } = useStores();
  const routeProjectSlug = useWorkAreaRouteRef();
  const { pathname } = useLocation();
  const shownWorkAreaId = useContext(ItemLinkWorkAreaContext);
  const itemHref = useItemHref();
  const here =
    workAreaRouteRefOf(shownWorkAreaId ? projectStore.projectMap?.[shownWorkAreaId] : undefined) ??
    routeProjectSlug ??
    workAreaRouteRefOf(projectStore.selectedProject);
  const allWorkAreas = isWorkAreaOverviewPath(pathname);
  const scopeItemHref = useAllWorkAreasItemHref();
  const phone = usePhoneNavigation();

  return useCallback(
    (item: ILinkedItem) => {
      const workArea = item.workAreaId
        ? projectStore.projects?.find((project) => project.id === item.workAreaId)
        : undefined;
      const slug = workAreaRouteRefOf(workArea);
      const elsewhere = Boolean(workArea && slug && slug !== here);
      // I-0656: labelled as the server reads a cross-work-area reference, `namespace/slug:KEY`.
      const label =
        elsewhere && workArea
          ? `${workArea.namespace ? `${workArea.namespace}/${workArea.slug}` : workArea.slug}:${item.key}`
          : item.key;
      // I-1304: a decision opens as a decision does from its list in this scope — beside All work
      // areas' or the organisation's list, else in its work area — never on the item route.
      if (item.itemTypeKey === DECISION_TYPE_KEY) {
        const ref = elsewhere ? slug : here;
        const organisation = namespaceIdOfPath(pathname);
        if (ref && allWorkAreas) return { href: workAreasDecisionPath(ref, item.key), label };
        if (ref && organisation) {
          return { href: namespaceDecisionPath(organisation, ref, item.key), label };
        }
        if (ref) return { href: projectDecisionPath(ref, item.key), label };
      }
      /*
        I-1357 (IL-19a): under All work areas a desktop opens a row in the panel over the page
        (I-0689), but a link is the reader asking for the item: its full page, in its work area.
        A phone's address is `useAllWorkAreasItemHref`'s, the item's pushed page (I-1304).
      */
      const ownRef = slug ?? here;
      if (allWorkAreas && !phone && ownRef) return { href: itemPagePath(ownRef, item.key), label };
      if (!workArea || !slug || !elsewhere) return { href: itemHref(item.key), label };
      // I-1304: on a phone, another work area's item at its own pushed address.
      if (allWorkAreas) return { href: scopeItemHref(workArea.id, item.key), label };
      return { href: projectItemPath(slug, item.key), label };
    },
    [itemHref, here, projectStore, allWorkAreas, scopeItemHref, pathname, phone],
  );
}
